import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiExternalLink, FiGithub, FiCode, FiZap, FiUsers, FiMessageCircle } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';

const ProjectsSection = styled.section<{ $isDarkMode: boolean }>`
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  background: ${props => props.$isDarkMode 
    ? 'rgba(26,26,46,0.92)' 
    : 'rgba(255,255,255,0.92)'
  };
  border: ${props => props.$isDarkMode 
    ? '1px solid rgba(255, 255, 255, 0.1)' 
    : '1px solid rgba(139, 92, 246, 0.1)'
  };
  border-radius: 24px;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
    border-radius: 16px;
    margin: 0 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled(motion.p)<{ $isDarkMode: boolean }>`
  font-size: 1.2rem;
  color: ${props => props.$isDarkMode ? '#94a3b8' : '#64748b'};
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FilterButtons = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 2rem;
  }
`;

const FilterButton = styled.button<{ active: boolean }>`
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' 
    : 'transparent'};
  color: ${props => props.active ? '#ffffff' : '#8b5cf6'};
  border: 2px solid #8b5cf6;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #8b5cf6, #ec4899);
    color: white;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;


const ProjectCard = styled(motion.div)<{ $isDarkMode: boolean }>`
  background: ${props => props.$isDarkMode 
    ? 'rgba(139, 92, 246, 0.1)' 
    : 'rgba(139, 92, 246, 0.05)'
  };
  border: ${props => props.$isDarkMode 
    ? '1px solid rgba(139, 92, 246, 0.2)' 
    : '1px solid rgba(139, 92, 246, 0.15)'
  };
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-10px);
    border-color: ${props => props.$isDarkMode 
      ? 'rgba(139, 92, 246, 0.4)' 
      : 'rgba(139, 92, 246, 0.3)'
    };
    box-shadow: 0 20px 40px rgba(139, 92, 246, 0.2);
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

const ProjectImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const ProjectIcon = styled.div`
  font-size: 3rem;
  color: white;
  z-index: 1;
`;

const ProjectContent = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ProjectTitle = styled.h3<{ $isDarkMode: boolean }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#1e293b'};
  margin-bottom: 1rem;

  /* Force visibility in light mode */
  [data-theme="light"] & {
    color: #1e293b !important;
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ProjectDescription = styled.p<{ $isDarkMode: boolean }>`
  font-size: 1rem;
  color: ${props => props.$isDarkMode ? '#94a3b8' : '#64748b'};
  line-height: 1.6;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const TechTag = styled.span`
  background: rgba(139, 92, 246, 0.2);
  color: #8b5cf6;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.2rem 0.6rem;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ProjectLink = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    color: #ec4899;
    transform: translateX(5px);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0;
    font-size: 0.9rem;
  }
`;


const FeaturedProject = styled(motion.div)`
  grid-column: 1 / -1;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.15));
  border: 2px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    padding: 2.5rem;
  }


  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    margin-bottom: 2rem;
    border-radius: 16px;
  }
`;

const FeaturedTitle = styled.h3<{ $isDarkMode: boolean }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#1e293b'};
  margin-bottom: 1rem;

  /* Force visibility in light mode */
  [data-theme="light"] & {
    color: #1e293b !important;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FeaturedDescription = styled.p<{ $isDarkMode: boolean }>`
  font-size: 1.1rem;
  color: ${props => props.$isDarkMode ? '#94a3b8' : '#64748b'};
  line-height: 1.6;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const Projects: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 9,
      title: 'MILLY - FRAMER',
      link: 'https://inquisitive-figolla-2cb69b.netlify.app',
      github: '',
      icon: <FiCode />,
      category: 'web',
      description: 'A Framer-based project for creative design.',
      tech: ['Framer', 'React']
    },
    {
      id: 2,
      title: 'SCHOOL TEMPLATE',
      link: 'https://christian-ministry.framer.website/',
      github: '',
      icon: <FiUsers />,
      category: 'web',
      description: 'Online platform for education.',
      tech: ['Framer', 'React']
    },
    {
      id: 3,
      title: 'QUANTUM FAITH',
      link: 'https://faith-in-divinity.framer.ai/',
      github: '',
      icon: <FiZap />,
      category: 'web',
      description: 'Educational growth (Spiritual - Online platform) and community resources.',
      tech: ['Framer', 'React']
    },
    {
      id: 1,
      title: 'ZONIA BOT',
      link: 'https://zonia.framer.ai/',
      github: '',
      icon: <FiMessageCircle />,
      category: 'ai',
      description: 'A chatbot with mood background.',
      tech: ['Framer', 'AI']
    },
    {
      id: 5,
      title: 'LOZZ',
      link: 'https://lozz.framer.ai/',
      github: '',
      icon: <FiCode />,
      category: 'web',
      description: 'Design or  Download free logo.',
      tech: ['Framer', 'Design']
    },
    {
      id: 6,
      title: 'BELBRI JOBS',
      link: 'https://belbri-jobs.framer.website/',
      github: '',
      icon: <FiUsers />,
      category: 'web',
      description: 'Job portal for various opportunities.',
      tech: ['Framer', 'React']
    },
    {
      id: 7,
      title: 'BELBRI WRITING ASSISTANT',
      link: 'https://belbri-sa.framer.ai/',
      github: '',
      icon: <FiZap />,
      category: 'ai',
      description: 'Writing assistant powered by AI.',
      tech: ['Framer', 'AI']
    },
    {
      id: 8,
      title: 'AI Chat Assistant',
      link: 'https://example.com',
      github: 'https://github.com/',
      icon: <FiMessageCircle />,
      category: 'ai',
      description: 'An intelligent chatbot powered by OpenAI with natural language processing.',
      tech: ['Python', 'OpenAI API', 'Flask', 'WebSocket']
    },
    {
      id: 4,
      title: 'Portfolio Website',
      link: 'https://briddy-tech.framer.ai/',
      github: 'https://github.com/portfolio',
      icon: <FiCode />,
      category: 'web',
      description: 'A modern, responsive portfolio website with React and styled-components.',
      tech: ['React', 'TypeScript', 'Styled Components', 'Framer Motion']
    }
  ].sort((a, b) => a.id - b.id);

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'web', label: 'Web Apps' },
    { id: 'ai', label: 'AI & Automation' },
    { id: 'automation', label: 'Automation' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <ProjectsSection id="projects" ref={ref} $isDarkMode={isDarkMode}>
      <SectionTitle
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={itemVariants}
      >
        My Projects
      </SectionTitle>
      
      <SectionSubtitle
        $isDarkMode={isDarkMode}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={itemVariants}
      >
        Here are some of the projects I've worked on
      </SectionSubtitle>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <FeaturedProject variants={itemVariants}>
          <FeaturedTitle $isDarkMode={isDarkMode} className="force-visible-text">Featured Project: TrackerR</FeaturedTitle>
          <FeaturedDescription $isDarkMode={isDarkMode}>
            My latest project, TrackerR, is a comprehensive tracking and analytics platform 
            that helps businesses monitor their performance and make data-driven decisions. 
            Built with modern technologies and a focus on user experience.
          </FeaturedDescription>
          <ProjectLinks>
            <ProjectLink
              href="https://app-trackerr.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiExternalLink size={20} />
              View Live
            </ProjectLink>
            <ProjectLink
              href="#"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiGithub size={20} />
              Source Code
            </ProjectLink>
          </ProjectLinks>
        </FeaturedProject>

        <FilterButtons variants={itemVariants}>
          {filters.map((filter) => (
            <FilterButton
              key={filter.id}
              active={activeFilter === filter.id}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </FilterButton>
          ))}
        </FilterButtons>

        <AnimatePresence mode="wait">
          <ProjectsGrid
            key={activeFilter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                $isDarkMode={isDarkMode}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ProjectImage>
                  <ProjectIcon>{project.icon}</ProjectIcon>
                </ProjectImage>
                <ProjectContent>
                  <ProjectTitle $isDarkMode={isDarkMode} className="force-visible-text">{project.title}</ProjectTitle>
                  <ProjectDescription $isDarkMode={isDarkMode}>{project.description}</ProjectDescription>
                  <ProjectTech>
                    {project.tech.map((tech, index) => (
                      <TechTag key={index}>{tech}</TechTag>
                    ))}
                  </ProjectTech>
                  <ProjectLinks>
                    {project.link && (
                      <ProjectLink
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FiExternalLink size={16} />
                        View Project
                      </ProjectLink>
                    )}
                    <ProjectLink href={project.github}>
                      <FiGithub size={16} />
                      Code
                    </ProjectLink>
                  </ProjectLinks>
                </ProjectContent>
              </ProjectCard>
            ))}
          </ProjectsGrid>
        </AnimatePresence>
      </motion.div>
    </ProjectsSection>
  );
};

export default Projects; 